import {
  ASTERISK_POLYGON_POINTS,
  ASTERISK_STROKE_WIDTH,
  ASTERISK_VIEWBOX,
  FOX_FACE_PATH,
  FOX_FACE_VIEWBOX,
  OFFER_EXPORT_ASTERISK_PX,
  OFFER_EXPORT_MARK_AXIS_PX,
  OFFER_EXPORT_SEMIBOLD_CSS,
  OFFER_EXPORT_FONT_BODY_CSS,
  OFFER_EXPORT_FONT_DISPLAY_CSS,
  OFFER_EXPORT_HEX,
  OFFER_EXPORT_VERTICAL_HEAD_BOTTOM_PX,
} from "@/lib/offer-export-brand";
import type { LeftPanelBlock } from "@/lib/offer-export-terms";

/** Panelets topp-padding fra malen. */
const PANEL_PAD_TOP = 41;

/**
 * Sentrerer tekstblokken loddrett: to like spacere deler restplassen over og
 * under. Blir teksten lengre enn panelet, krymper begge til null i stedet for
 * å skyve toppen ut — da faller blokken tilbake til å ligge i toppen, som
 * før, i stedet for å bli klippet i begge ender av panelets overflow: hidden.
 */
const CENTERING_SPACER = { flex: "1 1 0", minHeight: 0 } as const;

export function OfferExportAsterisk({ size = 28 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={ASTERISK_VIEWBOX}
      width={size}
      height={size}
      aria-hidden="true"
      style={{ display: "block", flexShrink: 0 }}
    >
      <polygon
        fill="none"
        stroke={OFFER_EXPORT_HEX.rosa}
        strokeWidth={ASTERISK_STROKE_WIDTH}
        strokeLinejoin="miter"
        points={ASTERISK_POLYGON_POINTS}
      />
    </svg>
  );
}

export function OfferExportFoxBadge({ size = 48 }: { size?: number }) {
  const fox = Math.round(size * 0.72);
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: OFFER_EXPORT_HEX.reveblaa,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={FOX_FACE_VIEWBOX}
        width={fox}
        height={fox}
        aria-hidden="true"
        style={{ display: "block", overflow: "visible" }}
      >
        <path fill="#ffffff" d={FOX_FACE_PATH} />
      </svg>
    </div>
  );
}

interface LeftPanelProps {
  /**
   * Vannrett overskrift. `null` for hovedbudsjettet — den roterte
   * «budsjett_» sier allerede hva sliden er (Erik 29.09). Tilvalg beholder
   * sin, fordi navnet på tilvalget ikke står noe annet sted.
   */
  title: string | null;
  subtitle: string;
  blocks: LeftPanelBlock[];
  pageLabel?: string | null;
}

/**
 * Venstre mørk panel, målt mot `Solvrev_Tilbudsmal_2026_2.pptx` (VIB-2556).
 *
 * Malen er 26,67 × 15", altså dobbelt av vårt 13,333 × 7,5"-lerret, som på
 * 1280 px blir 96 px per tomme. Malens mål, normalisert:
 *   panel 13,44" → 6,72" → 645 px (50,4 % av bredden)
 *   asterisk 1,49" → 0,745" → 71 px, plassert på x 71, y 56
 *   tekstblokk x 3,63" → 1,815" → 174 px, y 0,85" → 41 px, bredde 400 px
 *
 * Asterisken står altså TIL VENSTRE for tekstblokken i malen, ikke over
 * den — derfor den absolutte plasseringen i stedet for normal flyt.
 */
export function OfferBudgetImageLeft({
  title,
  subtitle,
  blocks,
  pageLabel,
}: LeftPanelProps) {
  return (
    <div
      style={{
        width: 645,
        padding: `${PANEL_PAD_TOP}px 36px 48px 174px`,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        position: "relative",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: "absolute",
          // Malen setter «budsjett_» i 50 pt på sitt dobbelt store lerret,
          // altså 25 pt hos oss = 33 px på 96 px/tomme, i brødtekstfonten.
          // Den står på linje med asterisken: målt flukter sentrene når
          // `left` er 132, siden rotasjonen om nedre venstre hjørne legger
          // linjeboksen til venstre for dette punktet.
          left: 132,
          // Erik 29.09: sto for høyt. Senkes like mye som hovedteksten
          // flyttes ned, så de to holder følge (bottom minker når den går ned).
          bottom: OFFER_EXPORT_VERTICAL_HEAD_BOTTOM_PX,
          transform: "rotate(-90deg)",
          transformOrigin: "left bottom",
          // Malen bruker brødtekstfonten her, ikke display-fonten.
          fontFamily: OFFER_EXPORT_FONT_BODY_CSS,
          fontSize: 33,
          fontWeight: 500,
          letterSpacing: 0.6,
          whiteSpace: "nowrap",
          color: OFFER_EXPORT_HEX.darkText,
        }}
      >
        budsjett_
      </div>
      {/*
        Merket beholder malens plass i hjørnet, men er krympet fra 42 px
        (Erik 29.09: «ikonet i venstre hjørne er for stort»).
      */}
      {/*
        Merket sentreres PÅ AKSEN i stedet for å ha hardkodet venstrekant.
        Da den ble krympet 29.09 med venstrekanten i behold, gled senteret
        til venstre og flukten med «budsjett_» røk (Erik 29.09).
      */}
      <div
        style={{
          position: "absolute",
          left: OFFER_EXPORT_MARK_AXIS_PX - OFFER_EXPORT_ASTERISK_PX / 2,
          top: 64,
        }}
      >
        <OfferExportAsterisk size={OFFER_EXPORT_ASTERISK_PX} />
      </div>
      {/* Erik 29.09: teksten lå for høyt — blokken sentreres nå loddrett. */}
      <div style={CENTERING_SPACER} />
      {title ? (
        <h1
          style={{
            fontFamily: OFFER_EXPORT_FONT_DISPLAY_CSS,
            fontSize: title.length > 18 ? 18 : 21,
            fontWeight: 600,
            margin: 0,
            letterSpacing: -0.3,
            color: OFFER_EXPORT_HEX.darkText,
          }}
        >
          {title}
        </h1>
      ) : null}
      {subtitle ? (
        <div
          style={{
            fontFamily: OFFER_EXPORT_FONT_DISPLAY_CSS,
            fontSize: 20,
            fontWeight: 500,
            color: OFFER_EXPORT_HEX.rosa,
            marginTop: 2,
            marginBottom: 20,
          }}
        >
          {subtitle}
        </div>
      ) : (
        <div style={{ height: 20 }} />
      )}
      {pageLabel ? (
        <div
          style={{
            fontSize: 11,
            color: OFFER_EXPORT_HEX.mutedText,
            marginBottom: 12,
            fontFamily: OFFER_EXPORT_FONT_BODY_CSS,
          }}
        >
          {pageLabel}
        </div>
      ) : null}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {blocks.map((block) => (
          <div
            key={block.key}
            style={{
              fontFamily: OFFER_EXPORT_FONT_BODY_CSS,
              fontSize: 20,
              lineHeight: 1.45,
              color: OFFER_EXPORT_HEX.darkText,
              fontWeight: block.bold ? OFFER_EXPORT_SEMIBOLD_CSS : 400,
              whiteSpace: "pre-wrap",
            }}
          >
            {block.text}
          </div>
        ))}
      </div>
      <div style={CENTERING_SPACER} />
    </div>
  );
}
